import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, type Data, NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, map, mergeMap } from 'rxjs/operators';

import { environment } from '../../environments/environment';

import { BrowserApiService } from './browser-api.service';

export interface SeoData extends Data {
  breadcrumb?: string;
  description?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  // =============================================================================
  // Private Properties
  // =============================================================================

  private readonly siteName = 'Darena';
  private readonly defaultDescription =
    'Professional dance competition management platform. Organize, score, and view results in real-time.';

  private readonly titleService = inject(Title);
  private readonly metaService = inject(Meta);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly transloco = inject(TranslocoService);
  private readonly document = inject(DOCUMENT);
  private readonly browserApi = inject(BrowserApiService);

  // =============================================================================
  // Constructor
  // =============================================================================

  constructor() {
    this.init();
  }

  // =============================================================================
  // Private Methods
  // =============================================================================

  private init(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route: ActivatedRoute) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route: ActivatedRoute) => route.outlet === 'primary'),
        mergeMap((route: ActivatedRoute) => route.data),
      )
      .subscribe((data: SeoData) => {
        this.updateSeo(data);
      });
  }

  private updateSeo(data: SeoData): void {
    // 1. Title handling
    let title = this.siteName;
    if (data['breadcrumb']) {
      const translated = this.transloco.translate(data['breadcrumb']);
      // Handle cases where breadcrumb might have params like "Contest {{id}}"
      // For simplicity, we just use the translated string if it doesn't look like a template
      if (translated && !translated.includes('{{')) {
        title = `${translated} | ${this.siteName}`;
      }
    }
    this.titleService.setTitle(title);

    // 2. Meta Tags
    const description = data['description']
      ? this.transloco.translate(data['description'])
      : this.transloco.translate('home.subtitle') || this.defaultDescription;

    this.updateMetaTags(title, description);

    // 3. Canonical Link
    this.updateCanonicalLink();

    // 4. Structured Data
    this.updateStructuredData(title, description);
  }

  private updateCanonicalLink(): void {
    const url = this.getCurrentUrl();

    if (!url) return;

    const linkId = 'canonical-link';
    let link = this.document.getElementById(linkId) as HTMLLinkElement;

    if (!link) {
      link = this.document.createElement('link');
      link.id = linkId;
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }

    link.href = url;
  }

  private getCurrentUrl(): string {
    return this.browserApi.isBrowser
      ? this.browserApi.location?.href?.split('?')[0] || ''
      : `${environment.appUrl?.replace(/\/$/, '') || ''}${this.router.url?.split('?')[0] || ''}`;
  }

  private updateMetaTags(title: string, description: string): void {
    const url = this.getCurrentUrl();

    type MetaTag = { name: string; content: string } | { property: string; content: string };

    const tags: MetaTag[] = [
      { name: 'description', content: description },
      // Open Graph
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: url || '' },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: this.siteName },
      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];

    tags.forEach((tag) => {
      if ('name' in tag) {
        this.metaService.updateTag({
          name: tag.name,
          content: tag.content,
        });
      } else {
        this.metaService.updateTag({
          property: tag.property,
          content: tag.content,
        });
      }
    });
  }

  private updateStructuredData(title: string, description: string): void {
    const scriptId = 'seo-json-ld';
    let script = this.document.getElementById(scriptId) as HTMLScriptElement;

    if (!script) {
      script = this.document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }

    const origin = this.browserApi.isBrowser
      ? this.browserApi.location?.origin
      : environment.appUrl || '';

    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: this.siteName,
      url: origin,
      description: description,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${origin}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };

    script.text = JSON.stringify(jsonLd);
  }
}
